// src/routes/index.tsx - Módosítás a Customers útvonal hozzáadásához
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import ProtectedRoute from "../components/common/ProtectedRoute";
import CustomerProfile from "../pages/customer/Profile";
import { CircularProgress } from "@mui/material";
import ManualPoints from "../pages/shop/ManualPoints";
import Rewards from "../pages/shop/Rewards";

// Lazy loading az oldalaknak
const Login = lazy(() => import("../pages/auth/Login"));
const Landing = lazy(() => import("../pages/Landing"));
const CustomerRegister = lazy(() => import("../pages/auth/CustomerRegister"));
const ShopRegister = lazy(() => import("../pages/auth/ShopRegister"));
const ShopHome = lazy(() => import("../pages/shop/Home"));
const ShopProfile = lazy(() => import("../pages/shop/Profile"));
const ShopEmployees = lazy(() => import("../pages/shop/Employees"));
const ShopScanner = lazy(() => import("../pages/shop/Scanner"));
const ShopTransactions = lazy(() => import("../pages/shop/Transactions"));
const ShopCustomers = lazy(() => import("../pages/shop/Customers")); // Új import
const NotFound = lazy(() => import("../pages/NotFound"));
const Unauthorized = lazy(() => import("../pages/Unauthorized"));
const ChangePassword = lazy(() => import("../pages/auth/ChangePassword"));
const ShopDetails = lazy(() => import("../pages/customer/ShopDetails"));
const ShopQRCode = lazy(() => import("../pages/customer/ShopQRCode"));
const CustomerHome = lazy(() => import("../pages/customer/Home"));
const CustomerPoints = lazy(() => import("../pages/customer/Points"));
const EmailVerify = lazy(() => import("../pages/auth/EmailVerify"));
const ForgotPassword = lazy(() => import("../pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("../pages/auth/ResetPassword"));
const NearbyShops = lazy(() => import("../pages/customer/NearbyShops"));
const Favorites = lazy(() => import("../pages/customer/Favorites"));

const Loading = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
    }}
  >
    <CircularProgress />
  </div>
);

const AppRouter = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* Nyilvános útvonalak */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register/customer" element={<CustomerRegister />} />
          <Route path="/register/shop" element={<ShopRegister />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify" element={<EmailVerify />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Vásárlói útvonalak */}
          <Route
            path="/customer"
            element={
              <ProtectedRoute allowedRoles={["customer"]}>
                <CustomerHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/profile"
            element={
              <ProtectedRoute allowedRoles={["customer"]}>
                <CustomerProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/points"
            element={
              <ProtectedRoute allowedRoles={["customer"]}>
                <CustomerPoints />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/shops/:shopId"
            element={
              <ProtectedRoute allowedRoles={["customer"]}>
                <ShopDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/shops/:shopId/qrcode"
            element={
              <ProtectedRoute allowedRoles={["customer"]}>
                <ShopQRCode />
              </ProtectedRoute>
            }
            // Ha nem talál shop ID-t, irányítsa vissza a bolt listára
            errorElement={<Navigate to="/customer" replace />}
          />
          <Route
            path="/customer/nearby-shops"
            element={
              <ProtectedRoute allowedRoles={["customer"]}>
                <NearbyShops />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/favorites"
            element={
              <ProtectedRoute allowedRoles={["customer"]}>
                <Favorites />
              </ProtectedRoute>
            }
          />

          {/* Üzleti útvonalak */}
          <Route
            path="/shop"
            element={
              <ProtectedRoute allowedRoles={["shop_admin", "shop_employee"]}>
                <ShopHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shop/profile"
            element={
              <ProtectedRoute allowedRoles={["shop_admin"]}>
                <ShopProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shop/employees"
            element={
              <ProtectedRoute allowedRoles={["shop_admin"]}>
                <ShopEmployees />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shop/scanner"
            element={
              <ProtectedRoute allowedRoles={["shop_admin", "shop_employee"]}>
                <ShopScanner />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shop/transactions"
            element={
              <ProtectedRoute allowedRoles={["shop_admin"]}>
                <ShopTransactions />
              </ProtectedRoute>
            }
          />
          {/* Új útvonal a vásárlók kezelésére */}
          <Route
            path="/shop/customers"
            element={
              <ProtectedRoute allowedRoles={["shop_admin"]}>
                <ShopCustomers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shop/manual-points"
            element={
              <ProtectedRoute allowedRoles={["shop_admin", "shop_employee"]}>
                <ManualPoints />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shop/rewards"
            element={
              <ProtectedRoute allowedRoles={["shop_admin", "shop_employee"]}>
                <Rewards />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer/change-password"
            element={
              <ProtectedRoute allowedRoles={["customer"]}>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shop/change-password"
            element={
              <ProtectedRoute allowedRoles={["shop_admin", "shop_employee"]}>
                <ChangePassword />
              </ProtectedRoute>
            }
          />

          {/* 404 oldal */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default AppRouter;
